import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import ExpensesDashboard from "@/components/ExpensesDashboard";
import AddExpenseDialog from "@/components/AddExpenseDialog";
import BankStatementUpload from "@/components/BankStatementUpload";
import UserProfile from "@/components/UserProfile";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import { cn } from "@/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { format, subMonths, parseISO, isValid, isBefore, compareAsc } from "date-fns";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, TooltipProps, ResponsiveContainer } from 'recharts';
import { PieChart, Pie, Cell, Legend } from 'recharts';
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const Dashboard = () => {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);

  // Generate a consistent color based on organization name
  const getOrganizationColor = (name: string) => {
    const colors = [
      'bg-[#9b87f5]', // Primary Purple
      'bg-[#7E69AB]', // Secondary Purple
      'bg-[#0EA5E9]', // Ocean Blue
      'bg-[#1EAEDB]', // Bright Blue
      'bg-[#6E59A5]', // Tertiary Purple
    ];
    
    const hash = name.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    
    return colors[Math.abs(hash) % colors.length];
  };

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/login");
      } else {
        setUser(session.user);
      }
    };
    checkAuth();
  }, [navigate]);

  const { data: organization } = useQuery({
    queryKey: ["organization", organizationId],
    queryFn: async () => {
      if (!organizationId) return null;
      
      const { data, error } = await supabase
        .from("organizations")
        .select("*")
        .eq("id", organizationId)
        .single();
      
      if (error) throw error;
      return data;
    },
    enabled: !!organizationId,
  });

  const { data: organizations } = useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("*");
      
      if (error) throw error;
      return data;
    },
  });

  // Updated query to fetch both regular expenses and expense report items
  const { data: expenses } = useQuery({
    queryKey: ["dashboard-expenses", organizationId],
    queryFn: async () => {
      if (!organizationId) return [];
      
      const startDate = subMonths(new Date(), 6);
      
      // First, fetch regular expenses
      const { data: regularExpenses, error: expenseError } = await supabase
        .from('expenses')
        .select(`
          *,
          categories (
            name,
            color
          )
        `)
        .eq('organization_id', organizationId)
        .gte('date', startDate.toISOString())
        .order('date', { ascending: false });
      
      if (expenseError) throw expenseError;
      
      // Fetch approved expense reports
      const { data: approvedReports, error: reportError } = await supabase
        .from("expense_reports")
        .select("id, status, total_amount, currency, date_from, date_to, created_at")
        .eq("organization_id", organizationId)
        .eq("status", "approved")
        .gte("date_from", startDate.toISOString());
      
      if (reportError) throw reportError;
      
      // Fetch expense report items if there are any approved reports
      let reportItems = [];
      if (approvedReports && approvedReports.length > 0) {
        const reportIds = approvedReports.map(report => report.id);
        
        const { data: items, error: itemsError } = await supabase
          .from("expense_report_items")
          .select(`
            *,
            categories (
              name,
              color
            )
          `)
          .in("expense_report_id", reportIds);
        
        if (itemsError) throw itemsError;
        reportItems = items || [];
      }
      
      // Create report summary expenses for approved reports
      const reportSummaries = (approvedReports || []).map(report => ({
        id: parseInt(`8${report.id.substring(0, 8)}`, 16),
        organization_id: organizationId,
        amount: Number(report.total_amount),
        date: report.date_from,
        created_at: report.created_at,
        description: `Expense Report #${report.id.substring(0, 8)} (Summary)`,
        currency: report.currency || 'USD',
        categories: {
          name: "Expense Report",
          color: "#4A7AFF"
        },
        is_report_summary: true
      }));
      
      // Combine all expenses
      return [...(regularExpenses || []), ...reportSummaries];
    },
    enabled: !!organizationId,
  });

  // Improved data processing for charts
  const monthlyData = useMemo(() => {
    if (!expenses || expenses.length === 0) return [];
    
    // Create a map to store monthly totals
    const monthMap = new Map();
    
    // Process each expense and group by month
    expenses.forEach(expense => {
      try {
        // Make sure we have a valid date
        const expenseDate = typeof expense.date === 'string' ? parseISO(expense.date) : new Date(expense.date);
        
        if (!isValid(expenseDate)) {
          console.warn("Invalid date found:", expense.date);
          return;
        }
        
        // Format the month as 'MMM yyyy'
        const monthKey = format(expenseDate, 'yyyy-MM');
        const displayMonth = format(expenseDate, 'MMM yyyy');
        
        // Initialize the month in our map if it doesn't exist
        if (!monthMap.has(monthKey)) {
          monthMap.set(monthKey, {
            monthKey,
            month: displayMonth,
            total: 0,
          });
        }
        
        // Add the expense amount to the monthly total
        monthMap.get(monthKey).total += Number(expense.amount);
      } catch (error) {
        console.error("Error processing expense date:", error, expense);
      }
    });
    
    // Convert the map to an array and sort by date
    const monthlyArray = Array.from(monthMap.values())
      .sort((a, b) => compareAsc(new Date(`${a.monthKey}-01`), new Date(`${b.monthKey}-01`)));
    
    return monthlyArray;
  }, [expenses]);

  // Improved pie chart data processing
  const pieData = useMemo(() => {
    if (!expenses || expenses.length === 0) return [];
    
    const categoryMap = new Map();
    
    expenses.forEach(expense => {
      const categoryName = expense.categories?.name || 'Uncategorized';
      
      if (!categoryMap.has(categoryName)) {
        categoryMap.set(categoryName, {
          name: categoryName,
          value: 0,
          color: expense.categories?.color || '#CCCCCC'
        });
      }
      
      categoryMap.get(categoryName).value += Number(expense.amount);
    });
    
    // Convert to array and sort by value (descending)
    return Array.from(categoryMap.values())
      .sort((a, b) => b.value - a.value);
  }, [expenses]);

  // Calculate totals
  const totalExpenses = expenses?.reduce((sum, expense) => sum + Number(expense.amount), 0) || 0;
  const averageExpense = expenses?.length ? totalExpenses / expenses.length : 0;

  // Custom formatter for tooltips to safely handle different value types
  const formatTooltipValue = (value: ValueType) => {
    if (typeof value === 'number') {
      return `$${value.toFixed(2)}`;
    }
    return value;
  };

  return (
    <div className="min-h-screen bg-background">
      <Header 
        user={user}
        organization={organization}
        organizations={organizations}
      />

      <div className="flex min-h-[calc(100vh-4rem)]">
        <Sidebar className="border-r border-border/50" />
        <main className={cn(
          "flex-1 container mx-auto px-4 py-8 space-y-8",
          "relative z-10 bg-background"
        )}>
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Expenses</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  ${totalExpenses.toFixed(2)}
                </div>
                <p className="text-xs text-muted-foreground">Last 6 months</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Average Expense</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  ${averageExpense.toFixed(2)}
                </div>
                <p className="text-xs text-muted-foreground">Per transaction</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Transactions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{expenses?.length || 0}</div>
                <p className="text-xs text-muted-foreground">Last 6 months</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <Card className="p-6">
              <CardHeader>
                <CardTitle>Monthly Expense Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {monthlyData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthlyData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip 
                          formatter={(value: ValueType) => [formatTooltipValue(value), 'Amount']} 
                        />
                        <Bar dataKey="total" fill="#3b82f6" name="Amount" />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex h-full items-center justify-center text-muted-foreground">
                      No expense data available
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="p-6">
              <CardHeader>
                <CardTitle>Expenses by Category</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  {pieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={80}
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell 
                              key={`cell-${index}`} 
                              fill={entry.color || COLORS[index % COLORS.length]} 
                            />
                          ))}
                        </Pie>
                        <Legend />
                        <Tooltip 
                          formatter={(value: ValueType) => [formatTooltipValue(value), 'Amount']} 
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="flex h-full items-center justify-center text-muted-foreground">
                      No category data available
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div className="flex items-center gap-4">
              {organizationId && (
                <AddExpenseDialog organizationId={organizationId} />
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Dashboard;
